// src/services/additional-api.ts

import {
  RANK_SEARCH_API,
  CONTACT_USERS_API,
  KEYWORD_SEARCH_API,
  ENQUETE_SEARCH_API,
  ENQUETE_SELECT_INPUT_ASSIST_API,
  FOLLOW_EDIT_API,
  APPOINT_LIST_API,
  PURPOSE_LIST_API,
} from '../config';
import { logApiError } from '../utils/errorLogger';
import { fetchWithTokenRefresh } from './token-manager';
import { isObjectData, setMasterCache, withMasterCache } from './storage/masterCache';

// ランク一覧のキャッシュキー（通信環境が悪い時の初期表示ブロック回避用）
const MASTER_RANKS_KEY = 'masterRanks';
// アシストテキスト一覧のキャッシュキー接頭辞（targetFunc ごとに分ける）
const MASTER_ASSIST_TEXTS_KEY_PREFIX = 'masterAssistTexts_';
// キーワード一覧のキャッシュキー接頭辞（configfunctiondiv ごとに分ける）
const MASTER_KEYWORDS_KEY_PREFIX = 'masterKeywords_';
// 担当者一覧のキャッシュキー（現役＋退職者の全件を保持する）
const MASTER_CONTACT_USERS_KEY = 'masterContactUsers';
// アポイント一覧のキャッシュキー
const MASTER_APPOINTS_KEY = 'masterAppoints';
// 目的一覧のキャッシュキー
const MASTER_PURPOSES_KEY = 'masterPurposes';

// アポイント／目的の1件分（divvalue/divvaluename の区分値ペア）
export interface DivValueOption {
  divvalue: string;
  divvaluename: string;
  defaultflag?: boolean;
}

const isArray = (data: unknown): data is any[] => Array.isArray(data);

// 担当者の型定義（画面側で実際に使用しているフィールドのみに絞っている。
// APIレスポンス自体は他にも多数のフィールド（アバター画像等）を含むが、
// キャッシュ肥大化を避けるため取得時点で以下だけに詰め替える）
export interface ContactUser {
  userno: number;
  username: string;
  deptno: number;
  deptname: string;
  mailaddress: string;
}

// APIレスポンスの担当者1件分を ContactUser（必要フィールドのみ）へ詰め替える
const pickContactUser = (raw: any): ContactUser => ({
  userno: raw.userno,
  username: raw.username,
  deptno: raw.deptno,
  deptname: raw.deptname,
  mailaddress: raw.mailaddress,
});

// 退職者部署No（acs-light の Constants.RETIRED_DEPT_NO）。
// 退職者は専用部署に移されるため、退職者含みで取得した一覧を deptno で現役／退職者に分けられる。
//
// 注意: -9999 は Constants.ACS_COMPANY_ADMIN_USER_NO（ACS企業管理者のユーザーNo）にも
// 登場する値だが、そちらは userno であり別物。判定は必ず deptno のみを見る。
const RETIRED_DEPT_NO = -9999;
const isRetiredUser = (u: ContactUser): boolean => u.deptno === RETIRED_DEPT_NO;

/**
 * 担当者の全件（現役＋退職者）を現役と退職者に分ける。
 *
 * 退職者は全件からの導出値なのでキャッシュには持たず、使う直前にここで分ける。
 *
 * @param all getContactUsers が返す全件
 */
export const splitContactUsers = (all: ContactUser[]): { active: ContactUser[]; retired: ContactUser[] } => ({
  active: all.filter(u => !isRetiredUser(u)),
  retired: all.filter(isRetiredUser),
});

// Additional API endpoints for AdditionalInfoPage
export const additionalAPI = {
  /**
   * 担当者一覧を「退職者含みの全件」で取得する。
   * キャッシュがあれば即座に返しつつ、TTL を過ぎていれば裏で最新を取り直す
   * （stale-while-revalidate）。通信環境が悪い時でも初期表示をブロックしない。
   */
  getContactUsers: async () => {
    // ネットワークから取得し、成功時はキャッシュへ保存する。失敗時は throw する
    // （withMasterCache 側でキャッシュ有無に関わらず同じ経路を通すため）
    const fetchFresh = async (): Promise<ContactUser[]> => {
      try {
        const res = await fetchWithTokenRefresh(CONTACT_USERS_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ includeretiree: true, includesystem: false }),
        });
        const data = await res.json();
        if (data?.statuscode === 200) {
          const contactUsers: ContactUser[] = (Array.isArray(data.contactuserlist) ? data.contactuserlist : []).map(pickContactUser);
          setMasterCache(MASTER_CONTACT_USERS_KEY, contactUsers).catch(() => {});
          return contactUsers;
        }
        throw new Error(data.message || '担当者一覧の取得に失敗しました');
      } catch (error: any) {
        logApiError('additionalAPI.getContactUsers', error, CONTACT_USERS_API, 'POST');
        throw error;
      }
    };

    try {
      const data = await withMasterCache(MASTER_CONTACT_USERS_KEY, isArray, fetchFresh);
      return { success: true, data };
    } catch (error: any) {
      return { success: false, message: error?.message || '担当者一覧の取得に失敗しました' };
    }
  },

  /**
   * ランク一覧を取得する。
   * キャッシュがあれば即座に返しつつ、TTL を過ぎていれば裏で最新を取り直す
   * （stale-while-revalidate）。通信環境が悪い時でも初期表示をブロックしない。
   */
  getRankList: async () => {
    // ネットワークから取得し、成功時はキャッシュへ保存する。失敗時は throw する
    const fetchFresh = async (): Promise<any[]> => {
      try {
        const res = await fetchWithTokenRefresh(RANK_SEARCH_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (data?.statuscode === 200 && Array.isArray(data.ranklist)) {
          const ranks = data.ranklist.map((r: any) => ({ id: r.rankno, name: r.rankname }));
          setMasterCache(MASTER_RANKS_KEY, ranks).catch(() => {});
          return ranks;
        }
        throw new Error(data.message || 'ランク一覧の取得に失敗しました');
      } catch (error: any) {
        logApiError('additionalAPI.getRankList', error, RANK_SEARCH_API, 'POST');
        throw error;
      }
    };

    try {
      const data = await withMasterCache(MASTER_RANKS_KEY, isArray, fetchFresh);
      return { success: true, data };
    } catch (error: any) {
      return { success: false, message: error?.message || 'ランク一覧の取得に失敗しました' };
    }
  },

  /**
   * キーワード一覧を取得する
   * @param configFunctionDiv 設定機能区分（例: "01"=備考欄用, "02"=フォロー登録用）
   */
  getKeywordList: async (
    configFunctionDiv?: string
  ): Promise<{ success: boolean; data?: any; message?: string }> => {
    const div = configFunctionDiv ?? '02';
    const cacheKey = `${MASTER_KEYWORDS_KEY_PREFIX}${div}`;

    // ネットワークから取得し、成功時はキャッシュへ保存する。失敗時は throw する
    const fetchFresh = async (): Promise<any> => {
      try {
        const res = await fetchWithTokenRefresh(KEYWORD_SEARCH_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ configfunctiondiv: div }),
        });
        const data = await res.json();
        if (data?.statuscode === 200) {
          setMasterCache(cacheKey, data).catch(() => {});
          return data;
        }
        throw new Error(data.message || 'キーワード一覧の取得に失敗しました');
      } catch (error: any) {
        logApiError('additionalAPI.getKeywordList', error, KEYWORD_SEARCH_API, 'POST');
        throw error;
      }
    };

    try {
      const data = await withMasterCache(cacheKey, isObjectData, fetchFresh);
      return { success: true, data };
    } catch (error: any) {
      return { success: false, message: error?.message || 'キーワード一覧の取得に失敗しました' };
    }
  },

  /**
   * アシスト入力用テキスト一覧を取得する
   * @param targetFunc 取得対象の機能（省略時はcustomerRemarks）
   */
  getAssistTextList: async (targetFunc?: string) => {
    const effectiveFunc = targetFunc ?? 'customerRemarks';
    const cacheKey = `${MASTER_ASSIST_TEXTS_KEY_PREFIX}${effectiveFunc}`;

    // ネットワークから取得し、成功時はキャッシュへ保存する。失敗時は throw する
    const fetchFresh = async (): Promise<any[]> => {
      try {
        const res = await fetchWithTokenRefresh(ENQUETE_SEARCH_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetFunc: effectiveFunc, isassist: true }),
        });
        const data = await res.json();
        if (data?.statuscode === 200) {
          const assistTexts = (data.enquetelist ?? []).map((e: any) => ({
            enqNo: e.enqNo,
            enqName: e.enqName,
          }));
          setMasterCache(cacheKey, assistTexts).catch(() => {});
          return assistTexts;
        }
        throw new Error(data.message || 'アシストテキスト一覧の取得に失敗しました');
      } catch (error: any) {
        logApiError('additionalAPI.getAssistTextList', error, ENQUETE_SEARCH_API, 'POST');
        throw error;
      }
    };

    try {
      const data = await withMasterCache(cacheKey, isArray, fetchFresh);
      return { success: true, data };
    } catch (error: any) {
      return { success: false, message: error?.message || 'アシストテキスト一覧の取得に失敗しました' };
    }
  },

  /**
   * アポイント一覧を取得する。
   * キャッシュがあれば即座に返しつつ、TTL を過ぎていれば裏で最新を取り直す
   * （stale-while-revalidate）。
   * @param onFresh 裏の再取得が成功したときにだけ呼ばれる（AppointmentTimePanel の
   *   defaultflag 自動選択のライブ反映用。省略時は裏取得結果を画面へ返さない）
   */
  getAppointList: async (onFresh?: (data: DivValueOption[]) => void) => {
    const fetchFresh = async (): Promise<DivValueOption[]> => {
      try {
        const res = await fetchWithTokenRefresh(APPOINT_LIST_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (data?.statuscode === 200 && Array.isArray(data.appointlist)) {
          setMasterCache(MASTER_APPOINTS_KEY, data.appointlist).catch(() => {});
          return data.appointlist;
        }
        throw new Error(data.message || 'アポイント一覧の取得に失敗しました');
      } catch (error: any) {
        logApiError('additionalAPI.getAppointList', error, APPOINT_LIST_API, 'POST');
        throw error;
      }
    };

    try {
      const data = await withMasterCache(MASTER_APPOINTS_KEY, isArray, fetchFresh, onFresh);
      return { success: true, data };
    } catch (error: any) {
      return { success: false, message: error?.message || 'アポイント一覧の取得に失敗しました' };
    }
  },

  /**
   * 目的一覧を取得する。
   * キャッシュがあれば即座に返しつつ、TTL を過ぎていれば裏で最新を取り直す
   * （stale-while-revalidate）。
   * @param onFresh 裏の再取得が成功したときにだけ呼ばれる（AppointmentTimePanel の
   *   defaultflag 自動選択のライブ反映用。省略時は裏取得結果を画面へ返さない）
   */
  getPurposeList: async (onFresh?: (data: DivValueOption[]) => void) => {
    const fetchFresh = async (): Promise<DivValueOption[]> => {
      try {
        const res = await fetchWithTokenRefresh(PURPOSE_LIST_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (data?.statuscode === 200 && Array.isArray(data.purposelist)) {
          setMasterCache(MASTER_PURPOSES_KEY, data.purposelist).catch(() => {});
          return data.purposelist;
        }
        throw new Error(data.message || '目的一覧の取得に失敗しました');
      } catch (error: any) {
        logApiError('additionalAPI.getPurposeList', error, PURPOSE_LIST_API, 'POST');
        throw error;
      }
    };

    try {
      const data = await withMasterCache(MASTER_PURPOSES_KEY, isArray, fetchFresh, onFresh);
      return { success: true, data };
    } catch (error: any) {
      return { success: false, message: error?.message || '目的一覧の取得に失敗しました' };
    }
  },

  /**
   * アシスト入力用テキストを取得する
   * @param enqNo 問い合わせ番号
   */
  getAssistText: async (enqNo: number) => {
    try {
      const res = await fetchWithTokenRefresh(ENQUETE_SELECT_INPUT_ASSIST_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enqueteNo: enqNo }),
      });
      const data = await res.json();
      return { success: true, data };
    } catch (error: any) {
      logApiError('additionalAPI.getAssistText', error, ENQUETE_SELECT_INPUT_ASSIST_API, 'POST');
      return { success: false, message: 'アシストテキストの取得に失敗しました' };
    }
  },

  /**
   * 追加情報を保存する
   * @param data 追加情報データ
   */
  saveAdditionalInfo: async (data: any) => {
    try {
      const res = await fetchWithTokenRefresh(FOLLOW_EDIT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const resData = await res.json();
      if (resData?.statuscode === 200) {
        return {
          success: true,
          message: 'フォローアップ情報を保存しました',
          data: { customerno: data.customerno, followdate: data.followdate, followcontent: data.followcontent },
        };
      }
      return { success: false, message: resData.message || '追加情報の保存に失敗しました' };
    } catch (error: any) {
      logApiError('additionalAPI.saveAdditionalInfo', error, FOLLOW_EDIT_API, 'POST');
      return { success: false, message: '追加情報の保存に失敗しました' };
    }
  },
};
